export const DEFAULT_CULTURE_LOCALE = "en-US";

const displayNamesCache = new Map<string, Intl.DisplayNames>();

const getDisplayNames = (
  type: "language" | "region" | "script",
  displayLocale: string,
): Intl.DisplayNames | undefined => {
  try {
    const locale = displayLocale.replace(/_/g, "-");
    const key = `${locale}:${type}`;
    let names = displayNamesCache.get(key);
    if (!names) {
      names = new Intl.DisplayNames([locale], { type });
      displayNamesCache.set(key, names);
    }
    return names;
  } catch (error) {
    console.warn(`getDisplayNames(${type}) failed`, error);
    return undefined;
  }
};

export const getCanonicalCultureLocale = (
  culture?: string | null,
): string | undefined => {
  const normalized = (culture || "").trim().replace(/_/g, "-");
  if (!normalized) {
    return undefined;
  }

  const [canonical] = Intl.getCanonicalLocales(normalized);
  return canonical;
};

export const formatCultureOptionLabel = (
  culture?: string | null,
  displayLocale = "en",
): string => {
  const rawValue = (culture || "").trim();
  const canonical = getCanonicalCultureLocale(rawValue);
  if (!canonical) {
    return rawValue;
  }

  try {
    const locale = new Intl.Locale(canonical);
    const languageName =
      getDisplayNames("language", displayLocale)?.of(locale.language) ||
      locale.language;
    const details = [locale.script, locale.region]
      .map((part) => {
        if (!part) return "";
        if (part === locale.script) {
          return getDisplayNames("script", displayLocale)?.of(part) || part;
        }
        return getDisplayNames("region", displayLocale)?.of(part) || part;
      })
      .filter(Boolean);

    return details.length > 0
      ? `${languageName} (${details.join(", ")}) - ${canonical}`
      : `${languageName} - ${canonical}`;
  } catch (error) {
    console.warn("formatCultureOptionLabel failed", error);
    return canonical;
  }
};

export const normalizeCultureLocale = (culture?: string | null): string => {
  const canonical = getCanonicalCultureLocale(culture);
  if (!canonical) {
    return DEFAULT_CULTURE_LOCALE;
  }

  try {
    return Intl.NumberFormat.supportedLocalesOf([canonical]).length > 0
      ? canonical
      : DEFAULT_CULTURE_LOCALE;
  } catch (error) {
    console.warn("normalizeCultureLocale failed", error);
    return DEFAULT_CULTURE_LOCALE;
  }
};
